import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Button, Divider, IconButton, Stack, Typography } from '@mui/material';
import { languageMap } from '../../utils/common';
import TranslationSuggestionCard from './TranslationSuggestionCard';
import HistoryTimeline from '../HistoryTimeline/HistoryTimeline';
import DeleteIcon from '@mui/icons-material/Delete';
import DeactivationDialog from './DeactivationDialog';
import EnglishStringInfoDialog from './EnglishStringInfoDialog';
import { useEffect, useMemo, useRef, useState } from 'react';
import { deactivateTranslationAndRefresh } from '../../store/slices/translationsSlice';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { closeTranslationDetails, closeDeactivationDialog, openDeactivationDialog } from '../../store/slices/uiSlice';
import { 
  selectDeactivationDialogOpen, 
  selectDetailsViewModel
} from '../../store/selectors/uiSelectors';
import { EditTranslation } from './EditTranslation';
import ConfirmationDialog from '../ConfirmationDialog';
import { useNavigate } from 'react-router-dom';
import { selectDeactivationLoading } from '../../store/selectors/translationsSelectors';
import ExpandableText from '../ExpandableText';

interface DetailsSheetProps {
  details: TranslationDetail;
}

const DetailsSheet: React.FC<DetailsSheetProps> = ({ details }) => {
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [isEditTranslationVisible, setIsEditTranslationVisible] = useState(false);

  const hasUnsavedChangesRef = useRef(false);
  const blockUpdateRef = useRef(false);
  const previousIdRef = useRef(details.translationRecordId);
  const pendingDetailsRef = useRef<TranslationDetail | null>(null);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const deactivationDialogOpen = useAppSelector(selectDeactivationDialogOpen);
  const viewModel = useAppSelector(selectDetailsViewModel);
  const deactivationLoading = useAppSelector(selectDeactivationLoading);
  
  // Extract view model properties
  const pasteEnabled = viewModel?.pasteEnabled ?? false;
  const editIconVisible = viewModel?.editIconVisible ?? false;
  const pendingVisible = viewModel?.pendingCard.visible ?? false;
  const outerDeactivateVisible = viewModel?.outerDeactivateVisible ?? false;
  const dialogButtons = viewModel?.dialogButtons;
  const status = viewModel?.status;

  const handleDrawerClose = () => {
    if (hasUnsavedChangesRef.current) {
      setShowConfirmationDialog(true);
    } else {
      setIsEditTranslationVisible(false);
      dispatch(closeTranslationDetails());
    }
  };

  const handleConfirmationClose = () => {
    setShowConfirmationDialog(false);
    pendingDetailsRef.current = null;
  };

  const handleConfirmationProceed = () => {
    setShowConfirmationDialog(false);
    setIsEditTranslationVisible(false);
    hasUnsavedChangesRef.current = false;
    blockUpdateRef.current = false;
    if (!pendingDetailsRef.current) {
      dispatch(closeTranslationDetails());
    } else { 
      pendingDetailsRef.current = null;
    }

  };

  useEffect(() => {
    const isTranslationSwitch = previousIdRef.current !== details.translationRecordId;
    if (isTranslationSwitch) {
      if (isEditTranslationVisible && hasUnsavedChangesRef.current) {
        setShowConfirmationDialog(true);
        pendingDetailsRef.current = details;
        blockUpdateRef.current = true;
      } else { 
        setIsEditTranslationVisible(false);
      }
      previousIdRef.current = details.translationRecordId;
    }
  }, [details.translationRecordId, isEditTranslationVisible]);

  // Create a memoized displayDetails value
  const displayDetails = useMemo(() => {
    return blockUpdateRef.current ? pendingDetailsRef.current ?? details : details;
  }, [details, blockUpdateRef.current]);

  const isApproved = viewModel ? status === "approved" : Boolean(details.approvedValue);
  const showSubmitNew = dialogButtons?.includes('SubmitNewString');

  const handleDeactivation = async () => {
    const postData = {
      translationRecordId: details.translationRecordId,
      language: details.language,
    };
      await dispatch(deactivateTranslationAndRefresh(postData)).unwrap();
      dispatch(closeDeactivationDialog());
      dispatch(closeTranslationDetails());
  }

  const handleEdit = () =>
  {
    setIsEditTranslationVisible(isEditTranslationVisible => !isEditTranslationVisible)
  }

  const handleSubmitTranslation = () => {
    navigate('/addtranslation');
  };

  return (
    <Box
      sx={styles.root}
      role="presentation"
    >
      <Stack>
        <Stack direction="row" sx={styles.header}>
          <Typography variant="h6">String content</Typography>
            <IconButton onClick={handleDrawerClose} size="small" aria-label="Close details">
              <CloseIcon />
            </IconButton>
        </Stack>

        <Stack sx={styles.publishedContainer} spacing={3}>
          <Typography variant="subtitle2" color="text.secondary" sx={styles.sectionTitle}>
            Currently published
          </Typography>

          <Stack spacing={0.5}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={styles.languageLabel}>
                English
              </Typography>
              <InfoOutlinedIcon color="primary" sx={styles.icon} onClick={() => setInfoDialogOpen(true)} />
            </Stack>
            <ExpandableText 
              text={displayDetails.originalString} 
            />
          </Stack>

          <Stack spacing={0.5}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={styles.languageLabel}>
                {languageMap[displayDetails.language]?.label}
              </Typography>
              <IconButton
                size="small"
                disabled={!pasteEnabled}
                aria-label="Copy Spanish translation"
                onClick={() => navigator.clipboard.writeText(displayDetails.approvedValue!)}
              >
                <ContentCopyIcon sx={styles.icon} />
              </IconButton>
              {editIconVisible && (
                <IconButton
                  size="small"
                  aria-label="Edit Spanish translation"
                  onClick={handleEdit}
                  disabled={isEditTranslationVisible}
                >
                  <EditOutlinedIcon sx={styles.icon} />
                </IconButton>
              )}
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
              {displayDetails.approvedValue ? (
                <ExpandableText 
                  text={displayDetails.approvedValue} 
                />
              ) : (
                <Typography variant="body1">-</Typography>
              )}
              {isApproved ? (
                <CheckCircleOutlineIcon color="success" sx={styles.icon} />
              ) : (
                <HighlightOffRoundedIcon color="error" sx={styles.icon} />
              )}
            </Stack>
            {isEditTranslationVisible && (
              <Stack>
                <EditTranslation 
                  currentDetails={displayDetails}
                  handleCloseEditTranslation={handleEdit}
                  onChangesPending={(hasPendingChanges) => hasUnsavedChangesRef.current = hasPendingChanges}
                />
              </Stack>
            )}
          </Stack>

          {pendingVisible && displayDetails.translationReview && (
            <TranslationSuggestionCard
              details={displayDetails}
            />
          )}

          {outerDeactivateVisible && (
            <Box>
              <Button onClick={() => dispatch(openDeactivationDialog())}>
                <DeleteIcon /> Deactivate Translation
              </Button>
            </Box>
          )}
        </Stack>
        <Divider />

        {displayDetails.history && (
          <Stack sx={styles.historyBox} spacing={3}>
            <Typography variant="subtitle2" color="text.secondary" sx={styles.sectionTitle}>
              History
            </Typography>
            <HistoryTimeline items={displayDetails.history} />
          </Stack>
        )}
      </Stack>

      <DeactivationDialog
        open={deactivationDialogOpen}
        onClose={() => dispatch(closeDeactivationDialog())}
        onDeactivate={handleDeactivation}
        loading={deactivationLoading}
      />

      <EnglishStringInfoDialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        showSubmitNewTranslation={showSubmitNew}
        onSubmitNewTranslation={handleSubmitTranslation}
      />

      <ConfirmationDialog
        open={showConfirmationDialog}
        onClose={handleConfirmationClose}
        onConfirm={handleConfirmationProceed}
        title="Do you want to abandon your edit?"
        message="Closing this drawer will delete any changes you have made to this translation."
        confirmText="Close Drawer"
        cancelText="Cancel"
        maxWidth="md"
      />
    </Box>
  )
};

const styles = {
  root: {
    width: '620px',
    bgcolor: '#fff',
    boxShadow: 'none',
    borderRadius: 0,
    position: 'relative',
  },
  header: {
    justifyContent: 'space-between',
    alignItems: 'center',
    p: 3,
  },
  publishedContainer: {
    px: 3,
    pt: 2,
    pb: 3
  },
  sectionTitle: {
    fontWeight: 400,
    fontSize: '1rem',
    color: '#757575', // TODO: Remove colors from here
  },
  publishedLabel: {
    fontWeight: 400,
    fontSize: '1rem',
    color: '#757575', // TODO: Remove colors from here
  },
  languageLabel: {
    textTransform: 'uppercase',
  },
  icon: {
    fontSize: '1rem',
  },
  historyLabel: {
    mt: 1,
  },
  historyBox: {
    p: 3,
  },
} as const;

export default DetailsSheet;
